import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../component/common/designSystem';
import {
  SCHEME_STATUS_COLORS,
  PERK_CLICKS_RAMP,
  schemeStatusChipClass
} from './perksTheme';

/**
 * Perks → Custom Perks.
 *
 * The perks the company added itself, and how its people engage with them.
 *
 * ENGAGEMENT HERE IS A ROLLUP, NOT A LEDGER. CustomOfferingEnrollment carries
 * `clickCount` and `lastClickedAt` but no per-click history, so there is a
 * "who opened it and how often" story and no click trend. The trend on this
 * page is therefore new enrollments, which is the thing that does have a
 * timestamp per row — a clicks-over-time chart would have to be invented.
 *
 * The number that matters most is the open rate: a perk everybody holds and
 * nobody opens is a different problem from one nobody holds.
 */
const CustomPerksDashboard = ({ fetcher, populationLabel = 'employees', onManagePerks }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const statusSplit = stats?.statusSplit || [];
  const clicksDistribution = stats?.clicksDistribution || [];
  const enrollmentsPerDay = stats?.enrollmentsPerDay || [];
  const topPerks = stats?.topPerks || [];
  const perks = stats?.perks || [];

  const trendLabels = lastNDaysLabels(enrollmentsPerDay.length);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.pink],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { show: false },
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} enrollments` } }
  };

  const topPerksOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topPerks.map((_perk, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topPerks.map((perk) => perk.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} clicks` } }
  };

  // People who hold a perk but never opened one.
  const neverOpened = Math.max((stats?.enrolled || 0) - (stats?.clickers || 0), 0);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Custom Perks | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Perks Offered"
          value={stats ? formatBigNumber(stats.totalPerks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Live"
          value={stats ? formatBigNumber(stats.approvedPerks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="People Holding One"
          value={stats ? formatBigNumber(stats.enrolled) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Opens"
          value={stats ? formatBigNumber(stats.totalClicks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Open Rate"
          value={stats ? `${stats.openRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Promoted"
          value={stats ? formatBigNumber(stats.promotedPerks) : '-'}
        />
      </div>

      {/* ---------- Reach and engagement ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-6">
        <BigStatPanel
          title="Perk Reach"
          subtitle={`Share of ${populationLabel} holding a perk`}
          value={stats ? `${stats.reach}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.enrolled)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />
        <MiniDonutCard
          title="Opens per Person"
          subtitle="How often each perk gets used"
          data={clicksDistribution}
          colors={PERK_CLICKS_RAMP}
          unit="perks held"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Perk Status"
          subtitle="Where each perk stands"
          data={statusSplit}
          colors={SCHEME_STATUS_COLORS}
          unit="perks"
          legendLimit={4}
        />
        {/*
          The engagement question, as two numbers: holding a perk and using it
          are different things, and only the second is worth the perk's cost.
        */}
        <Panel title="Engagement" subtitle="Holding against actually opening">
          <ProgressList
            items={[
              { label: 'Opened a perk', count: stats?.clickers || 0 },
              { label: 'Never opened one', count: neverOpened }
            ]}
            unit={populationLabel}
          />
        </Panel>
      </div>

      {/* ---------- Trend and ranking ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="New Perk Enrollments" subtitle="Per day — last 30 days">
          {enrollmentsPerDay.some((value) => value > 0) ? (
            <Chart
              type="area"
              height={340}
              width="100%"
              series={[{ name: 'Enrollments', data: enrollmentsPerDay }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No new perk enrollments in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Most Opened Perks" subtitle="Ranked by total opens">
          {topPerks.some((perk) => perk.clicks > 0) ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Opens', data: topPerks.map((perk) => perk.clicks) }]}
              options={topPerksOptions}
            />
          ) : (
            <PanelEmpty>No perks have been opened yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- The perks ---------- */}
      <div className="mt-6">
        <Panel
          title="Perk Breakdown"
          subtitle="Every perk you offer, with how many hold and open it"
          action={
            onManagePerks ? (
              <button
                type="button"
                onClick={onManagePerks}
                className="text-sm font-medium text-shoorah-primary hover:underline"
              >
                Manage perks
              </button>
            ) : null
          }
        >
          {perks.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Perk</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Holding</th>
                  <th className="py-2 pr-4 font-medium text-right">Opens</th>
                  <th className="py-2 font-medium">Last Opened</th>
                </>
              }
            >
              {perks.map((perk, index) => {
                // Engagement only lists perks somebody holds; a perk absent
                // from it has nobody, not an unknown number.
                const engagement = topPerks.find((row) => String(row.id) === String(perk.id));
                return (
                  <tr key={perk.id} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-2">
                        {/* Swatch ties the row to its bar in the ranking. */}
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: paletteAt(index) }}
                        />
                        <span className="truncate">
                          {perk.title}
                          {perk.isPromoted && (
                            <span className="ml-2 text-xs text-shoorah-gray3">Promoted</span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                          perk.status
                        )}`}
                      >
                        {perk.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right">
                      {engagement ? formatBigNumber(engagement.enrolled) : '-'}
                    </td>
                    <td className="py-2 pr-4 text-right font-semibold">
                      {engagement ? formatBigNumber(engagement.clicks) : '-'}
                    </td>
                    <td className="py-2">{engagement?.lastClickedAt || '-'}</td>
                  </tr>
                );
              })}
            </PanelTable>
          ) : (
            <PanelEmpty>No custom perks have been created for your company yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

CustomPerksDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string,
  /** Sends the admin to the existing perk management page. */
  onManagePerks: PropTypes.func
};

export default CustomPerksDashboard;
